import { CORE_PHYSICS, GAME_FRAME } from "../../config/gameplay";
import type { ProfileProgress } from "../progression/levels";
import type { CoreState } from "../runtime/types";
import type { OrbitForgeSave, PaidSupplyCrateId, SupplyCrateId, SupplyCrateResult } from "../save/types";
import type { UpgradeEffects } from "../upgrades/effects";
import {
  FREE_SUPPLY_COOLDOWN_SECONDS,
  PAID_CRATE_WINDOW_LIMIT,
  PAID_CRATE_WINDOW_SECONDS,
  SUPPLY_CRATE_UNLOCK_LEVEL,
  isPaidSupplyCrateId
} from "./catalog";
import { calculateFreeSupplyDropValue, calculatePaidSupplyCratePrice } from "./pricing";
import { rollSupplyCrateResult } from "./rolls";
import { createCoreFromCrate } from "../cores/factory";
import type { FeedbackDraft } from "../runtime/feedback";

export type SupplyCrateOpenStatus = "opened" | "locked" | "pending" | "cooldown" | "limited" | "unaffordable" | "unknown";
export type SupplyCratePresentStatus = "presented" | "missing" | "already-presented";

export interface SupplyCrateOpenCommandResult {
  readonly status: SupplyCrateOpenStatus;
  readonly dirty: boolean;
}

export interface SupplyCratePresentCommandInput {
  readonly save: OrbitForgeSave;
  readonly cores: readonly CoreState[];
  readonly effects: UpgradeEffects;
  readonly nowUnixSeconds: number;
}

export interface SupplyCratePresentCommandResult {
  readonly status: SupplyCratePresentStatus;
  readonly cores: readonly CoreState[];
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export function runOpenSupplyCrateCommand(
  save: OrbitForgeSave,
  crateId: SupplyCrateId,
  progress: ProfileProgress,
  nowUnixSeconds: number
): SupplyCrateOpenCommandResult {
  if (progress.effectiveLevel < SUPPLY_CRATE_UNLOCK_LEVEL) {
    return { status: "locked", dirty: false };
  }

  if (save.crates.pendingResult) {
    return { status: "pending", dirty: false };
  }

  if (crateId === "free") {
    return openFreeSupplyDrop(save, progress, nowUnixSeconds);
  }

  if (!isPaidSupplyCrateId(crateId)) {
    return { status: "unknown", dirty: false };
  }

  return openPaidSupplyCrate(save, crateId, progress, nowUnixSeconds);
}

export function runPresentSupplyCrateCommand(input: SupplyCratePresentCommandInput): SupplyCratePresentCommandResult {
  const result = input.save.crates.pendingResult;
  if (!result) {
    return { status: "missing", cores: input.cores, dirty: false, feedback: [] };
  }

  if (result.presentedAtUnixSeconds !== null) {
    return { status: "already-presented", cores: input.cores, dirty: false, feedback: [] };
  }

  input.save.stats.Credits += result.cash;
  grantTicketReward(input.save, result);
  applyCosmeticReward(input.save, result);
  if (result.jackpot) {
    input.save.stats.SupplyJackpotsWon += 1;
  }

  const presentedResult = { ...result, presentedAtUnixSeconds: input.nowUnixSeconds };
  input.save.crates = {
    ...input.save.crates,
    pendingResult: presentedResult,
    lastResult: presentedResult
  };

  const spawned = spawnCrateCores(input.save, input.cores, result);
  return {
    status: "presented",
    cores: spawned.cores,
    dirty: true,
    feedback: [
      { kind: "crate-reveal", target: "crate-reveal" },
      {
        kind: "floating-text",
        target: "crates",
        frameX: GAME_FRAME.width / 2,
        frameY: 268,
        color: result.jackpot ? "gold" : "cyan",
        text: result.jackpot ? `JACKPOT +${result.cash}` : `+${result.cash}`
      },
      ...spawned.feedback
    ]
  };
}

export function runDismissSupplyCrateResultCommand(save: OrbitForgeSave): SupplyCrateOpenCommandResult {
  if (!save.crates.pendingResult?.presentedAtUnixSeconds) {
    return { status: "pending", dirty: false };
  }

  save.crates = {
    ...save.crates,
    pendingResult: null
  };
  return { status: "opened", dirty: true };
}

function openFreeSupplyDrop(save: OrbitForgeSave, progress: ProfileProgress, nowUnixSeconds: number): SupplyCrateOpenCommandResult {
  if (nowUnixSeconds < save.crates.freeReadyAtUnixSeconds) {
    return { status: "cooldown", dirty: false };
  }

  const baseValue = calculateFreeSupplyDropValue(save, progress);
  const roll = rollSupplyCrateResult({
    save,
    crateId: "free",
    resultId: save.nextEntityId,
    openedAtUnixSeconds: nowUnixSeconds,
    pricePaid: 0,
    baseValue
  });

  save.nextEntityId += 1;
  save.rngState = roll.nextRngState;
  save.stats.FreeSupplyCratesOpened += 1;
  save.crates = {
    ...save.crates,
    freeReadyAtUnixSeconds: nowUnixSeconds + FREE_SUPPLY_COOLDOWN_SECONDS,
    pendingResult: roll.result
  };
  return { status: "opened", dirty: true };
}

function openPaidSupplyCrate(
  save: OrbitForgeSave,
  crateId: PaidSupplyCrateId,
  progress: ProfileProgress,
  nowUnixSeconds: number
): SupplyCrateOpenCommandResult {
  const window = currentPaidWindow(save, crateId, nowUnixSeconds);
  if (window.opens >= PAID_CRATE_WINDOW_LIMIT) {
    return { status: "limited", dirty: false };
  }

  const price = calculatePaidSupplyCratePrice(crateId, save, progress);
  if (save.stats.Credits < price) {
    return { status: "unaffordable", dirty: false };
  }

  const roll = rollSupplyCrateResult({
    save,
    crateId,
    resultId: save.nextEntityId,
    openedAtUnixSeconds: nowUnixSeconds,
    pricePaid: price,
    baseValue: price
  });

  save.nextEntityId += 1;
  save.rngState = roll.nextRngState;
  save.stats.Credits -= price;
  save.stats.SupplyCratesOpened += 1;
  save.crates = {
    ...save.crates,
    paidOpenWindows: {
      ...save.crates.paidOpenWindows,
      [crateId]: { startUnixSeconds: window.startUnixSeconds, opens: window.opens + 1 }
    },
    pendingResult: roll.result
  };
  return { status: "opened", dirty: true };
}

function currentPaidWindow(save: OrbitForgeSave, crateId: PaidSupplyCrateId, nowUnixSeconds: number) {
  const window = save.crates.paidOpenWindows[crateId];
  if (nowUnixSeconds - window.startUnixSeconds >= PAID_CRATE_WINDOW_SECONDS) {
    return { startUnixSeconds: nowUnixSeconds, opens: 0 };
  }
  return window;
}

function grantTicketReward(save: OrbitForgeSave, result: SupplyCrateResult): void {
  if (result.ticketSeconds === 60) save.stats.AutoLaunchTickets60 += 1;
  if (result.ticketSeconds === 120) save.stats.AutoLaunchTickets120 += 1;
  if (result.ticketSeconds === 600) save.stats.AutoLaunchTickets600 += 1;
}

function applyCosmeticReward(save: OrbitForgeSave, result: SupplyCrateResult): void {
  const cosmetic = result.cosmetic;
  if (!cosmetic || cosmetic.duplicate) {
    return;
  }

  if (cosmetic.kind === "shellSkin") {
    save.cosmetics = {
      ...save.cosmetics,
      SelectedShellSkin: cosmetic.id,
      UnlockedCrateShellSkins: [...save.cosmetics.UnlockedCrateShellSkins, cosmetic.id]
    };
  } else {
    save.cosmetics = {
      ...save.cosmetics,
      SelectedCorePattern: cosmetic.id,
      UnlockedCrateCorePatterns: [...save.cosmetics.UnlockedCrateCorePatterns, cosmetic.id]
    };
  }
}

function spawnCrateCores(save: OrbitForgeSave, cores: readonly CoreState[], result: SupplyCrateResult) {
  let nextCores = [...cores];
  const feedback: FeedbackDraft[] = [];
  for (let index = 0; index < result.coreBurstCount && nextCores.length < CORE_PHYSICS.hardActiveCoreCap; index += 1) {
    const rewardMultiplier = index === 0 ? (result.overchargedCoreMultiplier ?? 1) : 1;
    const spawn = createCoreFromCrate(save, rewardMultiplier);
    save.rngState = spawn.nextRngState;
    save.nextEntityId += 1;
    nextCores = [...nextCores, spawn.core];

    const frameX = spawn.core.position.x + GAME_FRAME.width / 2;
    const frameY = spawn.core.position.y + GAME_FRAME.height / 2;
    feedback.push({ kind: "core-spawn", frameX, frameY, color: spawn.core.color });
    if (spawn.core.isOvercharged) {
      feedback.push({
        kind: "rare-core",
        frameX,
        frameY: frameY - 34,
        color: "gold",
        text: `CRATE x${spawn.core.rewardMultiplier}`
      });
    }
  }
  return { cores: nextCores, feedback };
}
